import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBasket, Plus } from 'lucide-react';
import { useProducts } from '../../context/ProductContext';
import { useAuth } from '../../hooks/useAuth';
import { getUserShoppingLists } from '../../lib/firebase/firestore';
import { ShoppingList } from './ShoppingList';
import { AuthModal } from '../auth/AuthModal';
import { ShoppingListForm } from './ShoppingListForm';
import type { ShoppingList as ShoppingListType } from '../../types/ShoppingList';

export const ShoppingListContainer: React.FC = () => {
  const { user } = useAuth();
  const { products } = useProducts();
  const [lists, setLists] = useState<ShoppingListType[]>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLists = async () => {
    if (!user) {
      setLists([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userLists = await getUserShoppingLists(user.uid);
      setLists(userLists);
    } catch (err) {
      setError('Error loading shopping lists');
      console.error('Error fetching lists:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
  }, [user]);

  const handleListCreated = () => {
    fetchLists();
    setShowCreateForm(false);
  };

  const handleCreateClick = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      setShowCreateForm(true);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShoppingBasket className="text-orange-500" size={24} />
            <div>
              <h2 className="text-xl font-bold">Listas de la Compra</h2>
              <p className="text-gray-600">
                {user ? 'Gestiona tus listas de compra' : 'Inicia sesión para guardar tus listas'}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCreateClick}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <Plus size={20} />
            Nueva Lista
          </motion.button>
        </div>

        {showCreateForm && (
          <div className="mb-8">
            <ShoppingListForm onSuccess={handleListCreated} />
          </div>
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {loading ? (
            <div className="col-span-2 text-center py-12">
              <ShoppingBasket className="mx-auto text-orange-300 animate-spin mb-4" size={48} />
              <p className="text-gray-600">Cargando listas...</p>
            </div>
          ) : lists.length > 0 ? (
            lists.map(list => (
              <ShoppingList
                key={list.id}
                list={list}
                onDeleted={fetchLists}
                onUpdated={fetchLists}
              />
            ))
          ) : (
            <div className="col-span-2 text-center py-12 bg-orange-50 rounded-lg">
              <ShoppingBasket className="mx-auto text-orange-300 mb-4" size={48} />
              <p className="text-gray-600">
                {user 
                  ? 'No tienes listas de compra. ¡Crea una nueva!'
                  : 'Inicia sesión para crear y guardar tus listas de compra'}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};